import React, { useMemo } from 'react';
import { Card, Space, Typography, Divider } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import './styles.css';

const { Title, Text } = Typography;

export const CampaignPendingDetailsProgress = ({ campaigns, form }) => {
  // Scroll to a specific section smoothly
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Calculate progress for each campaign based on ad units/creatives
  const campaignProgress = useMemo(() => {
    return campaigns.map((campaign, index) => {
      // Check line items
      const lineItems = campaign.lineItems || [];
      const lineItemsProgress = lineItems.map((lineItem, lineItemIndex) => {
        // Check if line item has ad group
        const hasAdGroup = Boolean(lineItem.adGroup);

        // Check if ad group has creatives
        const creatives = lineItem.adGroup?.adGroupCreatives || [];
        const hasCreatives = creatives.length > 0;

        // Check creative media status
        const creativesStatus = creatives.map((creative, creativeIndex) => {
          const medias = creative.medias || [];

          // Check if creative has medias
          const hasMedias = medias.length > 0;

          // Check media upload status
          const mediasComplete = medias.every(media => {
            // Media is complete if it has a URL and is not processing
            return media.url && media.status !== 'PROCESSING' && media.status !== 'FAILED';
          });

          // Check if all medias are approved
          const mediasApproved = medias.length > 0 && medias.every(media => {
            return media.status === 'APPROVED';
          });

          const mediasPending = medias.some(media => media.status === 'PROCESSING');
          const mediasFailed = medias.some(media => media.status === 'FAILED');

          return {
            name: creative.name || `Creative ${creativeIndex + 1}`,
            hasMedias,
            mediasComplete,
            mediasApproved,
            mediasPending,
            mediasFailed,
            isComplete: hasMedias && mediasComplete && !mediasPending && !mediasFailed,
            isApproved: mediasApproved, // Already checks for medias.length > 0
          };
        });

        const allCreativesComplete = creativesStatus.length > 0 && creativesStatus.every(c => c.isComplete);
        const allCreativesApproved = creativesStatus.length > 0 && creativesStatus.every(c => c.isApproved);
        const anyCreativePending = creativesStatus.some(c => c.mediasPending);
        const anyCreativeFailed = creativesStatus.some(c => c.mediasFailed);

        return {
          name: lineItem.name || `Line Item ${lineItemIndex + 1}`,
          hasAdGroup,
          hasCreatives,
          creatives: creativesStatus,
          isComplete: hasAdGroup && hasCreatives && allCreativesComplete,
          isApproved: hasAdGroup && hasCreatives && allCreativesApproved,
          isPending: anyCreativePending,
          hasFailed: anyCreativeFailed,
        };
      });

      const allLineItemsComplete = lineItemsProgress.every(item => item.isComplete);
      const allLineItemsApproved = lineItemsProgress.every(item => item.isApproved);
      const anyLineItemPending = lineItemsProgress.some(item => item.isPending);
      const anyLineItemFailed = lineItemsProgress.some(item => item.hasFailed);

      return {
        name: campaign.name || `Campaign ${index + 1}`,
        isComplete: allLineItemsComplete,
        isApproved: allLineItemsApproved,
        isPending: anyLineItemPending,
        hasFailed: anyLineItemFailed,
        lineItems: lineItemsProgress,
      };
    });
  }, [campaigns]);

  // Helper function to get the appropriate icon based on status
  const getStatusIcon = (item) => {
    if (item.hasFailed) {
      return <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />;
    }
    if (item.isPending) {
      return <LoadingOutlined style={{ color: '#1890ff', fontSize: 16 }} />;
    }
    // Show green check only if approved, otherwise gray
    if (item.isApproved) {
      return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />;
    }
    return <CheckCircleOutlined style={{ color: '#d9d9d9', fontSize: 16 }} />;
  };

  const getCreativeStatusIcon = (creative) => {
    if (creative.mediasFailed) {
      return <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 12 }} />;
    }
    if (creative.mediasPending) {
      return <LoadingOutlined style={{ color: '#1890ff', fontSize: 12 }} />;
    }
    // Show green check only if approved, otherwise gray
    if (creative.isApproved) {
      return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />;
    }
    return <CheckCircleOutlined style={{ color: '#d9d9d9', fontSize: 12 }} />;
  };

  return (
    <Card
      title={<Title level={5}>CREATIVE STATUS</Title>}
      className="campaign-launch-progress"
      style={{
        position: 'sticky',
        top: 80,
        maxHeight: 'calc(100vh - 100px)',
        overflowY: 'auto',
      }}
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        {/* Details Section - Always complete */}
        <div
          onClick={() => scrollToSection('campaign-order-details')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            padding: '4px 0',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
          <Text>Details</Text>
        </div>

        {/* Duration & Budget Section - Always complete */}
        <div
          onClick={() => scrollToSection('campaign-order-details')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            padding: '4px 0',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
          <Text>Duration & Budget</Text>
        </div>

        {/* Categories Section - Always complete */}
        <div
          onClick={() => scrollToSection('campaign-order-details')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            padding: '4px 0',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
          <Text>Categories</Text>
        </div>

        {/* Attribution Reporting Section - Always complete */}
        <div
          onClick={() => scrollToSection('campaign-order-details')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            padding: '4px 0',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
          <Text>Attribution Reporting</Text>
        </div>

        {/* Campaigns */}
        {campaignProgress.map((campaign, index) => (
          <div key={index}>
            <Divider style={{ margin: '8px 0' }} />
            <div
              onClick={() => scrollToSection(`campaign-${index}`)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
                padding: '4px 0',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              {getStatusIcon(campaign)}
              <Text strong={!campaign.isComplete}>{campaign.name}</Text>
            </div>

            {/* Line Items under each campaign */}
            {campaign.lineItems.map((lineItem, lineItemIndex) => (
              <div key={lineItemIndex}>
                <div
                  onClick={() => scrollToSection(`campaign-${index}-lineitem-${lineItemIndex}`)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    paddingLeft: 24,
                    marginTop: 4,
                    cursor: 'pointer',
                    padding: '4px 0 4px 24px',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  {getStatusIcon(lineItem)}
                  <Text
                    type={!lineItem.isApproved ? 'secondary' : undefined}
                    strong={lineItem.isApproved}
                  >
                    {lineItem.name}
                  </Text>
                </div>

                {/* Creatives under each line item */}
                {lineItem.creatives.map((creative, creativeIndex) => (
                  <div
                    key={creativeIndex}
                    onClick={() => scrollToSection(`campaign-${index}-lineitem-${lineItemIndex}-creative-${creativeIndex}`)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      paddingLeft: 48,
                      marginTop: 2,
                      cursor: 'pointer',
                      padding: '4px 0 4px 48px',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    {getCreativeStatusIcon(creative)}
                    <Text
                      style={{ fontSize: 12 }}
                      type={!creative.isApproved ? 'secondary' : undefined}
                    >
                      {creative.name}
                      {!creative.hasMedias && <Text type="danger" style={{ fontSize: 11, marginLeft: 4 }}>(No media)</Text>}
                      {creative.mediasPending && <Text type="warning" style={{ fontSize: 11, marginLeft: 4 }}>(Processing)</Text>}
                      {creative.mediasFailed && <Text type="danger" style={{ fontSize: 11, marginLeft: 4 }}>(Failed)</Text>}
                      {!creative.isApproved && creative.hasMedias && !creative.mediasPending && !creative.mediasFailed && (
                        <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>(Pending approval)</Text>
                      )}
                    </Text>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </Space>
    </Card>
  );
};
